import classNames from 'classnames';
import { Button } from '@optimization/ssi-common';
import { ReactElement, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router';
import classes from './style.module.scss';
import Box from '@/common/components/Box';
import { MenuItemTitle } from '@/features/Solution/utils/solution-menu-items';
import { ModalTitle } from '@/app/context/ImportCandidateModalContext/types';
import useImportCandidateModal from '@/app/context/ImportCandidateModalContext/use-import-candidate-modal';

interface Properties {
  readonly modalTitle: ModalTitle;
  readonly optionTitle: MenuItemTitle;
  readonly optionDescription: string;
  readonly optionIcon: ReactNode;
  readonly disabled: boolean;
}

const VehicleImportOptionBox = ({
  modalTitle,
  optionTitle,
  optionDescription,
  optionIcon,
  disabled,
}: Properties): ReactElement => {
  const navigate = useNavigate();
  const { closeModal, solutionId } = useImportCandidateModal();

  const getDataTestId = useCallback((): string => {
    const normalizedTitle = optionTitle.toLowerCase().replaceAll(/\s+/g, '-');
    return `button-create-new-${normalizedTitle}`;
  }, [optionTitle]);

  const getButtonText = useCallback((): string => {
    switch (true) {
      case modalTitle === 'Add Candidates' && optionTitle === 'Use customer name': {
        return 'Add customer';
      }
      case modalTitle === 'Add Candidates' && optionTitle === 'Use chassis number': {
        return 'Add candidates';
      }
      default: {
        return 'Create new';
      }
    }
  }, [modalTitle, optionTitle]);

  const handleCreateNewClick = useCallback((): void => {
    closeModal();
    void navigate(`/import-candidates`, {
      state: { buttonType: optionTitle, modalTitle, solutionId },
    });
  }, [closeModal, navigate, optionTitle, modalTitle, solutionId]);

  return (
    <Box className={classNames(classes['vehicle-import-box'], { [classes['vehicle-import-box--disabled']]: disabled })}>
      <div className={classes['option-content']}>
        <span className={classes['option-icon']}>{optionIcon}</span>
        <div>
          <h4>{optionTitle}</h4>
          <p>{optionDescription}</p>
        </div>
      </div>
      <Button
        variant="secondary"
        text={getButtonText()}
        disabled={disabled}
        onClick={handleCreateNewClick}
        dataTestid={getDataTestId()}
      />
    </Box>
  );
};

export default VehicleImportOptionBox;
